import moment from "moment-timezone";

// Old names the browser still lists (CLDR) → current IANA names. Only renames:
// country aliases such as Europe/Bratislava → Europe/Prague stay as they are.
const RENAMED_TIMEZONES = {
  "Africa/Asmera": "Africa/Asmara",
  "America/Buenos_Aires": "America/Argentina/Buenos_Aires",
  "America/Catamarca": "America/Argentina/Catamarca",
  "America/Coral_Harbour": "America/Atikokan",
  "America/Cordoba": "America/Argentina/Cordoba",
  "America/Godthab": "America/Nuuk",
  "America/Indianapolis": "America/Indiana/Indianapolis",
  "America/Jujuy": "America/Argentina/Jujuy",
  "America/Louisville": "America/Kentucky/Louisville",
  "America/Mendoza": "America/Argentina/Mendoza",
  "Asia/Calcutta": "Asia/Kolkata",
  "Asia/Katmandu": "Asia/Kathmandu",
  "Asia/Rangoon": "Asia/Yangon",
  "Asia/Saigon": "Asia/Ho_Chi_Minh",
  "Atlantic/Faeroe": "Atlantic/Faroe",
  "Europe/Kiev": "Europe/Kyiv",
  "Pacific/Enderbury": "Pacific/Kanton",
  "Pacific/Ponape": "Pacific/Pohnpei",
  "Pacific/Truk": "Pacific/Chuuk",
};

/**
 * Current IANA name of a zone: Europe/Kiev → Europe/Kyiv.
 *
 * @param {string} timezone
 * @returns {string}
 */
export const getCanonicalTimezone = (timezone) => RENAMED_TIMEZONES[timezone] || timezone;

/**
 * Zones to choose from in the profile and on the chart: the browser's list
 * (Intl.supportedValuesOf) under their current names, only those moment-timezone
 * can draw. The old names are kept for search.
 *
 * @param {string[]} [extraTimezones] - zones that must be there (current, UTC)
 * @returns {{ timezone: string, aliases: string[] }[]}
 */
export const getTimezoneOptions = (extraTimezones = []) => {
  const supported =
    typeof Intl.supportedValuesOf === "function"
      ? Intl.supportedValuesOf("timeZone")
      : moment.tz.names();
  const aliasesByTimezone = new Map();

  [...extraTimezones, ...supported].forEach((name) => {
    const timezone = name && getCanonicalTimezone(name);
    if (!timezone || !moment.tz.zone(timezone)) return;

    const aliases = aliasesByTimezone.get(timezone) || [];
    if (name !== timezone && !aliases.includes(name)) aliases.push(name);
    aliasesByTimezone.set(timezone, aliases);
  });

  return [...aliasesByTimezone].map(([timezone, aliases]) => ({ timezone, aliases }));
};

export const convertToUserTimezone = (utcTime, format = "YYYY-MM-DD HH:mm:ss") => {
  const savedTimezone = localStorage.getItem("userTimezone");
  const userTimezone = savedTimezone ? savedTimezone : moment.tz.guess();

  return moment.utc(utcTime).tz(userTimezone).format(format);
};

export const getUserTimezone = () => {
  const savedTimezone = localStorage.getItem("userTimezone");
  return getCanonicalTimezone(savedTimezone ? savedTimezone : moment.tz.guess());
};

export const formatUTC = (timestamp, format = "YYYY-MM-DD-HH:mm") =>
  moment(timestamp * 1000).tz("UTC").format(format);

export const getAdjustedTime = (timestamp, offset, unit = "minute") =>
  moment(timestamp * 1000).tz("UTC").subtract(offset, unit).format("YYYY-MM-DD-HH:mm");

/**
 * The profile time zone saved in this browser (localStorage
 * "tradingview.chartproperties"). The profile and the chart read it first:
 * without a saved zone the backend sends the one by IP, and a zone saved on
 * the server is copied here when the user loads (syncSavedTimezone).
 *
 * @returns {string} IANA zone or "" when none is saved
 */
export const getTradingViewTimezone = () => {
  try {
    const raw = localStorage.getItem("tradingview.chartproperties");
    if (!raw) return "";

    const parsed = JSON.parse(raw);
    return typeof parsed?.timezone === "string" ? getCanonicalTimezone(parsed.timezone) : "";
  } catch (error) {
    return "";
  }
};

/**
 * Saves the profile time zone where the profile page reads it first
 * (localStorage "tradingview.chartproperties", see useProfileUserData).
 * Called after the time zone is saved to the profile.
 *
 * @param {string} nextTimezone - IANA zone
 */
export const upsertTradingViewTimezone = (nextTimezone) => {
  if (!nextTimezone) return;

  try {
    const raw = localStorage.getItem(
      "tradingview.chartproperties",
    );

    const parsed = raw ? JSON.parse(raw) : {};

    const safeObject =
      parsed &&
      typeof parsed === "object" &&
      !Array.isArray(parsed)
        ? parsed
        : {};

    localStorage.setItem(
      "tradingview.chartproperties",
      JSON.stringify({
        ...safeObject,
        timezone: nextTimezone,
      }),
    );
  } catch (error) {
    localStorage.setItem(
      "tradingview.chartproperties",
      JSON.stringify({
        timezone: nextTimezone,
      }),
    );
  }
};

/**
 * A zone the user saved on the server wins over the one saved in this
 * browser: it is written where the profile and the chart read it first.
 * Older backends send no timezone_saved, then the browser's zone stays.
 *
 * @param {object|null} user - user from GET /users/me
 */
export const syncSavedTimezone = (user) => {
  if (user?.timezone_saved && user.timezone) upsertTradingViewTimezone(user.timezone);
};
